import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getCookieValue,
    isIOS,
    setCookie,
    useMessenger,
} from '../context/messengerContext';
import { useLanguage } from '../context/localeContext';
import { getNativePlatformAuth } from '../utils/platformAuth';

const IS_INVITED_USER_KEY = 'is_invited_user';

export function getIsInvitedUserFromStorage() {
    const ios = isIOS();
    if (ios) {
        return getCookieValue(IS_INVITED_USER_KEY);
    }
    return localStorage.getItem(IS_INVITED_USER_KEY);
}

export function saveIsInvitedUserToStorage() {
    const ios = isIOS();
        
    // На iOS ВСЕГДА сохраняем в cookies (они копируются при установке PWA)
    // На других платформах сохраняем в localStorage
    if (ios) {
        setCookie(IS_INVITED_USER_KEY, 'true');
        console.log(`📱 iOS detected: saved max_id to cookie (will be copied on PWA install)`);
    } else {
        localStorage.setItem(IS_INVITED_USER_KEY, 'true');
        console.log(`💾 Non-iOS: saved max_id to localStorage`);
    }
}

/**
 * Парсит start_param из Telegram/Max deep linking.
 * start_param — JSON, закодированный в base64.
 */
function parseStartParam(raw) {
    if (!raw) return null;

    try {
        const decoded = atob(raw);
        return JSON.parse(decoded);
    } catch {
        return null;
    }
}

export function getAuthRedirect({ platform, payload, hasNativeAuth = false }) {
    if (hasNativeAuth && payload?.action !== 'need_auth_contact') {
        return { path: '/menu' };
    }

    if (platform === 'max' && payload?.action !== 'need_auth_contact') {
        return { path: '/auth' };
    }

    if (!payload || !payload.action) {
        return { path: '/menu' };
    }

    const { action, contract_number, cdek_id, lang } = payload;

    switch (action) {
        case 'need_auth':
            return { path: '/auth', state: { first: '1' } };

        case 'need_auth_contact':
            return {
                path: '/auth_contact',
                state: { contract_number, first: '1', language: lang },
            };

        case 'courier_error': {
            const courierParams = new URLSearchParams();
            if (contract_number) courierParams.set('contract_number', contract_number);
            if (cdek_id) courierParams.set('cdek_id', cdek_id);
            const courierQuery = courierParams.toString();
            return { path: `/courier${courierQuery ? `?${courierQuery}` : ''}` };
        }

        case 'open_chat': {
            const chatParams = new URLSearchParams();
            if (contract_number) chatParams.set('contract_number', contract_number);
            const chatQuery = chatParams.toString();
            return { path: `/enter_chat${chatQuery ? `?${chatQuery}` : ''}` };
        }

        case 'auth':
        default:
            return { path: '/menu' };
    }
}

export function useAuthRedirect() {
    const navigate = useNavigate();
    const { initDataUnsafe, platform } = useMessenger();
    const { setLanguage } = useLanguage();

    useEffect(() => {
        const startParam = initDataUnsafe?.start_param;
        const payload = parseStartParam(startParam);
        const { platform: nativePlatform, initData } = getNativePlatformAuth();

        const lang = payload?.lang;
        if (lang) {
            setLanguage(lang);
        }

        const redirect = getAuthRedirect({
            platform,
            payload,
            hasNativeAuth: Boolean(initData && nativePlatform === platform),
        });
        navigate(redirect.path, { replace: true, ...(redirect.state && { state: redirect.state }) });
    }, [initDataUnsafe, platform, navigate, setLanguage]);
}
